<template>
	<div class="flex flex-col lg:h-full" v-if="!isLoading">
		<router-view />
		<!-- router view for modals -->
		<BaseHeader :title="getStatusString" :subtitle="getSubtitleString">
			<div class="flex mr-10" v-if="isUserAllowedInLevel3">
				<div class="font-bold mr-4 text-lg">{{ $t('end_mandate_early') }}</div>
				<div>
					<Toggle style="vertical-align: bottom" :active="endMandateEarly" @click.native.prevent="setEndMandateEarly" />
				</div>
			</div>
			<div class="flex">
				<div class="font-bold mr-4 text-lg">{{ $t('process_active') }}</div>
				<div>
					<Toggle style="vertical-align: bottom" :active="!isProcessFinished" @click.native.prevent="setProcessFinished(!isProcessFinished)" />
				</div>
			</div>
		</BaseHeader>

		<div
			v-if="process"
			class="flex flex-grow relative"
			:style="
				isUserAllowedInLevel5or6 && $store.getters.getIsChatExpanded
					? 'margin-bottom: -500px'
					: isUserAllowedInLevel5or6
					? 'margin-bottom: -50px'
					: ''
			"
		>
			<div class="w-full">
				<CardWrapper class="main">
					<ProcessDatasheetCard
						:process="process"
						:is-toggle-open="getToggleStatus('processDatasheet')"
						:is-toggleable="true"
						@toggleCard="toggleCard('processDatasheet', $event)"
					/>
					<MiscellaneousCard
						:overview-image="overviewImage"
						:is-edit-allowed="getAuthenticatedUserGroupLevel !== '3' && !isUserAllowedInLevel2"
						:is-toggle-open="getToggleStatus('overviewImage')"
						:is-toggleable="true"
						@toggleCard="toggleCard('overviewImage', $event)"
					/>

					<DamageAssessorCard
						:damage-assessor-company="process.damageAssessorCompany"
						:damage-assessor="process.damageAssessor"
						:title="$t('damage_assessor').toString()"
						v-if="!isOrderDocumentation"
						:is-edit-allowed="(this.$store.getters.isProcessComprehensive || this.$store.getters.isProcessLiability) && !isUserAllowedInLevel3"
						edit-route="damagecases.edit.damageAssessor"
						:is-toggle-open="getToggleStatus('damageAssessor')"
						:is-toggleable="true"
						@toggleCard="toggleCard('damageAssessor', $event)"
					/>

					<OPOListCard
						:damage-case="process"
						v-if="getAuthenticatedUserGroupLevel === '2' || (getAuthenticatedUserGroupLevel === '3' && isSystemDamageAssessor)"
						:is-toggle-open="getToggleStatus('opoList')"
						:is-toggleable="true"
						@toggleCard="toggleCard('opoList', $event)"
					/>
					<OPORepairShopListCard
						:damage-case="process"
						v-if="getAuthenticatedUserGroupLevel === '5' || getAuthenticatedUserGroupLevel === '6' || getAuthenticatedUserGroupLevel === '3'"
						:is-toggle-open="getToggleStatus('opoList')"
						:is-toggleable="true"
						@toggleCard="toggleCard('opoList', $event)"
					/>

					<CompanyCard
						:is-process-finished="false"
						:company="process.lawyerOffice"
						:title="$t('lawyer_office_lawyer')"
						:is-edit-allowed="isUserAllowedInLevel5or6 || isUserAllowedInLevel1"
						edit-route="damagecases.edit.lawyerOffice"
						v-if="getAuthenticatedUserGroupLevel !== '3' && !isOrderDocumentation"
						:missing-value="$t('customer_no_lawyer')"
						:is-toggle-open="getToggleStatus('lawyerOffice')"
						:is-toggleable="true"
						@toggleCard="toggleCard('lawyerOffice', $event)"
						:mandate="endMandateEarly ? { showMandate: true, note: process.endLawyerMandate } : { showMandate: false }"
					/>

					<OppositeInsurenceCard
						:is-process-finished="false"
						:company="process.oppositeInsurance"
						:damage-case="process"
						:is-toggle-open="getToggleStatus('oppositeInsurance')"
						:is-toggleable="true"
						@toggleCard="toggleCard('oppositeInsurance', $event)"
						v-if="!isOrderDocumentation"
					/>

					<AccidentCard
						:damage-case="process"
						:is-process-finished="false"
						:is-toggle-open="getToggleStatus('accident')"
						:is-toggleable="true"
						@toggleCard="toggleCard('accident', $event)"
						v-if="!isOrderDocumentation"
					/>

					<ReferenceNumberCard
						:damage-case="process"
						:is-process-finished="false"
						:is-toggle-open="getToggleStatus('referenceNumber')"
						:is-toggleable="true"
						@toggleCard="toggleCard('referenceNumber', $event)"
					/>

					<PersonCard
						:is-edit-allowed="isUserAllowedInLevel1 || isUserAllowedInLevel5or6"
						v-if="isUserAllowedInLevel1 || isUserAllowedInLevel5or6"
						:title="$t('customer_consultant').toString()"
						:person="process.customerConsultant"
						edit-route="damagecases.edit.customerConsultant"
						:is-toggle-open="getToggleStatus('customerConsultant')"
						:is-toggleable="true"
						@toggleCard="toggleCard('customerConsultant', $event)"
					/>

					<CompanyCard
						:title="$store.getters.getDamageCaseRepairShopLabel"
						:company="process.repairShop"
						:customer-consultant="process.customerConsultant"
						:show-customer-consultant-phone-number="true"
						:show-customer-consultant-email="true"
						:is-process-finished="false"
						v-if="!isUserAllowedInLevel5or6"
						edit-route="damagecases.edit.repairShop"
						:is-edit-allowed="getAuthenticatedUserGroupLevel === '1' || isUserAllowedInLevel5or6"
						:is-toggle-open="getToggleStatus('repairShop')"
						:is-toggleable="true"
						@toggleCard="toggleCard('repairShop', $event)"
					>
						<template #icons>
							<button @click="openNebenleistungen" v-if="process.repairShop" :title="$t('additional_services')">
								<font-awesome-icon :icon="['fas', 'money-bill-wave']"></font-awesome-icon>
							</button>
						</template>
					</CompanyCard>
					<ProcessApprovalCard
						:process="process"
						v-if="isUserAllowedInLevel2"
						:is-toggle-open="getToggleStatus('approval')"
						@toggleCard="toggleCard('approval', $event)"
					/>

					<RepairExtensionCard
						v-if="
							(getAuthenticatedUserGroupLevel !== '3' && getAuthenticatedUserGroupLevel !== '2') ||
								(repairExtensions.length && getAuthenticatedUserGroupLevel === '2')
						"
						:is-process-finished="false"
						:repair-extensions="repairExtensions"
						:is-toggle-open="getToggleStatus('repairExtensions')"
						:is-toggleable="true"
						:process="process"
						@toggleCard="toggleCard('repairExtensions', $event)"
					/>

					<ProcessRepairPlanCard
						v-if="isUserAllowedInLevel5or6"
						:process="process"
						:is-toggle-open="getToggleStatus('repairPlan')"
						:is-toggleable="true"
						@toggleCard="toggleCard('repairPlan', $event)"
					/>

					<ImagesCard
						v-if="
							(getAuthenticatedUserGroupLevel !== '3' && getAuthenticatedUserGroupLevel !== '2') ||
								(previewWorkingImages.length && getAuthenticatedUserGroupLevel === '2')
						"
						:is-process-finished="false"
						:images="previewWorkingImages"
						:title="$t('work_images').toString()"
						edit-route="damagecases.edit.workingImages"
						:is-edit-allowed="(getAuthenticatedUserGroupLevel === '2' && previewWorkingImages.length !== 0) || getAuthenticatedUserGroupLevel !== '2'"
						:is-toggle-open="getToggleStatus('workingImages')"
						:is-toggleable="true"
						@toggleCard="toggleCard('workingImages', $event)"
					/>

					<ImagesCard
						v-if="
							(getAuthenticatedUserGroupLevel !== '3' && getAuthenticatedUserGroupLevel !== '2') ||
								(previewRepairProofImages.length && getAuthenticatedUserGroupLevel === '2')
						"
						:is-process-finished="false"
						:images="previewRepairProofImages"
						:title="$t('repair_proof_images').toString()"
						edit-route="damagecases.edit.repairProofs"
						:is-edit-allowed="
							(getAuthenticatedUserGroupLevel === '2' && previewRepairProofImages.length !== 0) || getAuthenticatedUserGroupLevel !== '2'
						"
						:is-toggle-open="getToggleStatus('repairProofs')"
						:is-toggleable="true"
						@toggleCard="toggleCard('repairProofs', $event)"
						:empty-message="$t('no_images_stored').toString()"
					/>

					<ProcessDocuments
						id="documentsection"
						class="col-span-2"
						style="scroll-margin-top: 80px"
						:process="process"
						:is-system-damage-assessor="isSystemDamageAssessor"
					/>
					<Note
						:notes="process.notes"
						:disabled="false"
						class="col-span-2"
						:is-toggle-open="getToggleStatus('note')"
						:is-toggleable="true"
						@toggleCard="toggleCard('note', $event)"
					/>
				</CardWrapper>
			</div>
		</div>
		<div v-else>
			<LoadingSpinner />
		</div>
		<div class="hidden">
			<CompanyVariablesList ref="CompanyVariablesListNebenleistungen" :company-variables="getNebenleistungen" v-if="getNebenleistungen" />
		</div>
		<Footer
			@backButtonClick="goBack"
			:back-button-title="
				isUserAllowedInLevel2 || isUserAllowedInLevel1 || isUserAllowedInLevel3 ? $t('to_processes').toString() : $t('to_customer_file').toString()
			"
			:is-back-button="true"
			:is-continue-button="false"
			:is-skippable="false"
			:show-chat="isUserAllowedInLevel5or6"
			:process="this.process"
			class="m-8"
			v-if="(isUserAllowedInLevel5or6 || isUserAllowedInLevel2 || isUserAllowedInLevel1 || isUserAllowedInLevel3) && process != null"
		/>
	</div>
	<LoadingSpinner v-else />
</template>

<script>
import Note from '@/components/damagecases/forms/Note';
import OPOListCard from '@/components/damagecases/cards/OPOListCard';
import AccidentCard from '@/components/damagecases/cards/AccidentCard';
import ReferenceNumberCard from '@/components/damagecases/cards/ReferenceNumberCard';
import RepairExtensionCard from '@/components/damagecases/cards/RepairExtensionCard';
import MiscellaneousCard from '@/components/damagecases/cards/MiscellaneousCard';
import PersonCard from '@/components/damagecases/cards/PersonCard';
import CompanyCard from '@/components/damagecases/cards/CompanyCard';
import ImagesCard from '@/components/damagecases/cards/ImagesCard';
import Authentication from '@/mixins/Authentication';
import { DamagecasesApiFactory, DamageCaseStateEnum } from '@/api/generated';
import apiDefaults from '@/api/defaultConfiguration';
import LoadingSpinner from '@/components/navigation/LoadingSpinner';
import DunningStatus from '@/mixins/damagecase/DunningStatus';
import Formatter from '@/mixins/Formatter';
import CompanyVariablesList from '@/components/damagecases/CompanyVariablesList';
import OppositeInsurenceCard from '@/components/damagecases/cards/OppositeInsuranceCard';
import ProcessDocuments from '@/views/management/Process/Document/ProcessDocuments';
import ProcessDatasheetCard from '@/views/management/Process/ProcessDatasheetCard';
import BaseHeader from '@/components/navigation/BaseHeader';
import ProcessRepairPlanCard from '@/views/management/Process/ProcessRepairPlanCard';
import Footer from '@/components/damagecases/Footer';
import CardWrapper from '@/components/damagecases/forms/CardWrapper';
import Toggle from '@/components/forms/Toggle.vue';
import ProcessApprovalCard from '@/views/management/Process/ProcessApprovalCard';
import DamageAssessorCard from '@/components/damagecases/cards/DamageAssessorCard.vue';
import DamageCaseRepository from '@/api/repositories/DamageCaseRepository';
import OPORepairShopListCard from '@/components/damagecases/cards/OPORepairshopListCard.vue';

export default {
	name: 'ManagementProcessEdit',
	components: {
		OPORepairShopListCard,
		DamageAssessorCard,
		ProcessApprovalCard,
		Toggle,
		CardWrapper,
		Footer,
		ProcessRepairPlanCard,
		BaseHeader,
		ProcessDatasheetCard,
		ProcessDocuments,
		OppositeInsurenceCard,
		CompanyVariablesList,
		LoadingSpinner,
		ImagesCard,
		CompanyCard,
		PersonCard,
		MiscellaneousCard,
		RepairExtensionCard,
		ReferenceNumberCard,
		AccidentCard,
		OPOListCard,
		Note,
	},
	mixins: [Authentication, DunningStatus, Formatter],
	data() {
		return {
			isProcessFinished: false,
			isDocSideBarOpen: false,
			toggleState: true,
			isLoading: true,
			scrolledToDocs: false,
			processCardsToggleStatus: {
				processDatasheet: true,
				overviewImage: true,
				damageAssessor: true,
				opoList: true,
				lawyerOffice: true,
				oppositeInsurance: true,
				accident: true,
				referenceNumber: true,
				customerConsultant: true,
				repairShop: true,
				repairExtensions: true,
				repairPlan: false,
				workingImages: true,
				repairProofs: true,
				note: true,
				approval: true,
			},
		};
	},
	computed: {
		isOrderDocumentation() {
			return this.process?.process_type === 'order_documentation';
		},
		isSystemDamageAssessor() {
			return this.process.damageAssessor?.system_assessor;
		},
		getNebenleistungen() {
			return this.process ? this.process.companyvariables : null;
		},
		repairExtensions() {
			return this.$store.getters.getRepairExtension || [];
		},
		previewWorkingImages() {
			return this.$store.getters.getDamageCaseWorkingImages || [];
		},
		overviewImage() {
			return this.$store.getters.getDamageCaseThumbnail?.base64Content;
		},
		endMandateEarly() {
			return this.process?.endLawyerMandate != null;
		},
		previewRepairProofImages() {
			const proofs = this.$store.getters.getRepairProof;
			if (
				!proofs ||
				!proofs.frontViewImage ||
				!proofs.leftSideViewImage ||
				!proofs.rearViewImage ||
				!proofs.rightSideViewImage ||
				!proofs.speedometerImage
			) {
				return [];
			}

			let images = [proofs.frontViewImage, proofs.leftSideViewImage, proofs.rearViewImage, proofs.rightSideViewImage, proofs.speedometerImage];

			if (proofs.optionalDamageImages.length > 0) {
				images = images.concat(proofs.optionalDamageImages);
			}

			return images;
		},
		process() {
			return this.$store.getters.getDamageCase;
		},
		getStatusString: function() {
			if (this.process?.status) {
				let title = this.getProcessTypeString();
				if (this.isUserAllowedInLevel2 && this.process.damageAssessorReferenceNumber != null) {
					title = title + ' - ' + this.process.damageAssessorReferenceNumber;
				}
				if (this.isUserAllowedInLevel5or6 && this.process.repairShopReferenceNumber != null) {
					title = title + ' - ' + this.process.repairShopReferenceNumber;
				}
				if (this.isUserAllowedInLevel3 && this.process.lawyerOfficeReferenceNumber != null) {
					title = title + ' - ' + this.process.lawyerOfficeReferenceNumber;
				}

				return title;
			}

			return this.$t('adjust');
		},
		getSubtitleString() {
			if (this.process?.status !== 'wait') {
				let assessorStatus = '';
				switch (this.process?.status) {
					case DamageCaseStateEnum.Repair100:
						assessorStatus = this.$t('permission_to_repair_100');
						break;
					case DamageCaseStateEnum.Repair130:
						assessorStatus = this.$t('permission_to_repair_130');
						break;
					case DamageCaseStateEnum.WriteOff:
						assessorStatus = this.$t('permission_to_repair_write_off');
						break;
				}
				return assessorStatus;
			} else {
				return null;
			}
		},
	},
	methods: {
		scrollToDocuments() {
			setTimeout(() => {
				//TODO: maybe put in store
				this.scrolledToDocs = true;
				document.getElementById('documentsection').scrollIntoView();
			}, 500);
		},
		getProcessTypeString() {
			if (this.process?.opposite_insurance_state === 'ohne_versicherung_selbstzahler') {
				return this.$t('customer_order');
			}
			switch (this.process.process_type) {
				case 'liability':
					return this.$t('liability');
				case 'comprehensive':
					return this.$t('comprehensive');
				case 'order_documentation':
					return `${this.$t('order_documentation')} (${this.process.orderDocumentationType.name})`;
				default:
					return '';
			}
		},
		goBack() {
			if (this.isUserAllowedInLevel5or6) {
				this.$router.push({ path: '/management/customer/overview/' + this.$route.params.customer });
			} else if (this.isUserAllowedInLevel2 || this.isUserAllowedInLevel1 || this.isUserAllowedInLevel3) {
				this.$router.push({ path: '/damagecases/' });
			}
		},
		updateData() {
			this.$store.dispatch('updateDamageCase', { id: this.$route.params.processId }).then(response => {
				// if customer is not set, redirect to url with customer id. Customer is not set if called from route: damagecases.edit
				if (this.$route.params.customer === '0') {
					this.$router.replace({
						name: 'management.process.edit',
						params: { customer: this.$store.getters.getDamageCase.customer.id, processId: this.$store.getters.getDamageCase.id },
					});
				}
				this.isProcessFinished = this.process?.isClosed;

				// default false, but from storages wins.
				this.processCardsToggleStatus.damageAssessor = !this.isOrderDocumentation;

				this.processCardsToggleStatus.lawyerOffice = !this.isOrderDocumentation;
				this.processCardsToggleStatus.oppositeInsurance = !this.isOrderDocumentation;
				this.processCardsToggleStatus.accident = !this.isOrderDocumentation;
				this.$store.commit('setProcessCardsToggleStatus', this.processCardsToggleStatus);
			});
			this.$store.commit('setRepairExtensionNeedUpdate', true);

			this.$store.dispatch('updateRepairProof', { id: this.$route.params.processId || this.$route.params.id });
			this.$store.dispatch('updateRepairExtension', { id: this.$route.params.processId || this.$route.params.id });
			this.$store.dispatch('updateDamageCaseThumbnail', { id: this.$route.params.processId || this.$route.params.id });
			this.$store.dispatch('updateDamageCaseWorkingImages', { id: this.$route.params.processId || this.$route.params.id });
			this.processCardsToggleStatus = this.$store.getters.getProcessCardsToggleStatus;
			this.isLoading = false;
		},
		getToggleStatus(card) {
			return this.processCardsToggleStatus?.[card];
		},
		toggleCard($card, $event) {
			this.processCardsToggleStatus[$card] = $event;
			this.$store.commit('setProcessCardsToggleStatus', this.processCardsToggleStatus);
		},
		closeDocumentSidebar() {
			if (this.$refs.documentSidebar) this.$refs.documentSidebar.closeMenu();
		},
		openNebenleistungen() {
			this.$swal({
				title: this.$t('additional_services').toString(),
				html: this.$refs['CompanyVariablesListNebenleistungen'].$el,
				width: '800px',
			});
		},
		setEndMandateEarly() {
			if (this.endMandateEarly === false) {
				this.$swal({
					title: this.$t('sure').toString(),
					text: this.$t('do_you_want_to_end_mandate_early').toString(),
					icon: 'warning',
					cancelButtonText: this.$t('general.cancel'),
					showCancelButton: true,
					confirmButtonText: this.$t('general.save'),
					input: 'textarea',
					validationMessage: this.$t('enter_reason').toString(),
					inputValidator: inputValue => {
						if (!inputValue) {
							return this.$t('enter_reason');
						}
					},
				}).then(result => {
					if (result.value) {
						new DamageCaseRepository()
							.setEndMandateEarly(this.process.id, { note: result.value })
							.then(response => {
								this.$store.dispatch('forceUpdateDamageCase', { id: parseInt(this.$route.params.processId || this.$route.params.id) }).then(_ => {
									this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success');
								});
							})
							.catch(e => {
								this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
							});
					}
				});
			} else {
				new DamageCaseRepository()
					.setEndMandateEarly(this.process.id, {})
					.then(response => {
						this.$store.dispatch('forceUpdateDamageCase', { id: parseInt(this.$route.params.processId || this.$route.params.id) }).then(_ => {
							this.$swal(this.$t('general.success').toString(), this.$t('general.changes_saved').toString(), 'success');
						});
					})
					.catch(e => {
						this.$swal(this.$t('general.error').toString(), this.$t('general.changes_not_saved').toString(), 'error');
					});
			}
		},
		setProcessFinished(state) {
			this.$swal({
				title: this.$t('sure').toString(),
				text: this.isProcessFinished ? this.$t('reactivate_process').toString() : this.$t('close_process').toString(),
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: this.$t('general.yes').toString(),
				cancelButtonText: this.$t('general.cancel').toString(),
			}).then(result => {
				if (result.value) {
					this.isProcessFinished = state;
					new DamageCaseRepository()
						.toggleClose(parseInt(this.$route.params.processId || this.$route.params.id))
						.then(response => {
							this.$store.dispatch('forceUpdateDamageCase', { id: parseInt(this.$route.params.processId || this.$route.params.id) });
						})
						.catch(e => {
							console.log(e);
						});
				}
			});
		},
	},
	watch: {
		'$route.params': {
			handler: function(search) {
				this.updateData();
			},
			deep: true,
			immediate: true,
		},
		process: {
			handler: function(value) {
				if (value != null && this.$route.params.scrollToDocs && !this.scrolledToDocs) {
					this.scrollToDocuments();
				}
			},
			deep: true,
			immediate: true,
		},
	},
};
</script>
